import { useEffect, useCallback } from "react";
import { useLocation, Link } from "react-router-dom";
import { useAuth } from "@/components/providers/auth-provider";
import { cn } from "@/lib/utils";
import {
  BarChart3,
  Bell,
  ChevronLeft,
  ChevronRight,
  FileText,
  MessageSquare,
  Settings,
} from "lucide-react";

interface SidebarProps {
  collapsed: boolean;
  onToggleCollapse: () => void;
  mobileOpen: boolean;
  onMobileClose: () => void;
}

interface NavItem {
  label: string;
  href: string;
  icon: React.ReactNode;
  subItems?: { label: string; href: string }[];
}

const mainNavItems: NavItem[] = [
  { label: "Dashboard", href: "/", icon: <BarChart3 className="w-5 h-5" /> },
  { label: "Licitaciones", href: "/licitaciones", icon: <FileText className="w-5 h-5" /> },
  { label: "Asistente", href: "/chat", icon: <MessageSquare className="w-5 h-5" /> },
];

// La entrada "Proveedores LLM" (con su uso/coste) es configuración
// operacional de la plataforma: solo existe para el rol `platform_admin`
// (operador). A los usuarios y admins del cliente ni se les muestra --
// el backend además la protege con require_platform_admin.
function buildSystemNavItems(isPlatformAdmin: boolean): NavItem[] {
  const configSubItems = [
    ...(isPlatformAdmin
      ? [
          { label: "Proveedores LLM", href: "/configuracion/llm" },
          // Panel de accesos: auditoría + adopción, solo operador.
          { label: "Accesos y seguridad", href: "/configuracion/accesos" },
        ]
      : []),
    // Mejoras L/I (changelog cliente): editor de plantillas de propuesta.
    { label: "Plantillas de propuesta", href: "/configuracion/templates" },
    // Mejora N2 (changelog cliente): catálogo de normativa vigente.
    { label: "Marco legal", href: "/configuracion/marco-legal" },
    // Mejora D (changelog cliente): gestión de equipos (admin).
    { label: "Equipos", href: "/configuracion/equipos" },
    // Mejora "Perfil empresa" (changelog cliente): se inyecta en agentes.
    { label: "Perfil de empresa", href: "/configuracion/perfil-empresa" },
    { label: "Perfil", href: "/configuracion/perfil" },
  ];
  return [
    // Analytics oculto: el cliente prefiere indicadores accionables en el
    // Dashboard sobre dashboards genéricos. La página sigue accesible por URL
    // directa para QA interno.
    { label: "Notificaciones", href: "/notificaciones", icon: <Bell className="w-5 h-5" /> },
    {
      label: "Configuraci\u00f3n",
      // El item padre apunta al primer subitem visible según el rol.
      href: configSubItems[0].href,
      icon: <Settings className="w-5 h-5" />,
      subItems: configSubItems,
    },
  ];
}

function isItemActive(pathname: string, item: NavItem): boolean {
  if (item.href === "/") return pathname === "/";
  return pathname.startsWith(item.href);
}

export function Sidebar({ collapsed, onToggleCollapse, mobileOpen, onMobileClose }: SidebarProps) {
  const { pathname } = useLocation();
  const { isPlatformAdmin } = useAuth();
  const systemNavItems = buildSystemNavItems(isPlatformAdmin);

  useEffect(() => {
    try { localStorage.setItem("sidebar-collapsed", JSON.stringify(collapsed)); } catch { /* ignore */ }
  }, [collapsed]);

  const handleBackdropClick = useCallback(() => onMobileClose(), [onMobileClose]);

  // Sistema de diseño Meridianos: sidebar sobre fondo `ink` con
  // texto claro, ítem activo en `brand` (teal #0099a0).
  const renderNavItem = (item: NavItem) => {
    const active = isItemActive(pathname, item);
    const showSubItems = item.subItems && pathname.startsWith("/configuracion") && !collapsed;

    return (
      <li key={item.href}>
        <Link
          to={item.href}
          onClick={onMobileClose}
          className={cn(
            "flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors",
            active
              ? "bg-brand text-white"
              : "text-white/75 hover:bg-white/10 hover:text-white",
            collapsed && "justify-center px-0"
          )}
          title={collapsed ? item.label : undefined}
        >
          <span className="flex-shrink-0">{item.icon}</span>
          {!collapsed && <span>{item.label}</span>}
        </Link>
        {showSubItems && (
          <ul className="mt-1 ml-8 space-y-0.5">
            {item.subItems!.map((sub) => (
              <li key={sub.href}>
                <Link
                  to={sub.href}
                  onClick={onMobileClose}
                  className={cn(
                    "block px-3 py-1.5 rounded-md text-sm transition-colors",
                    pathname === sub.href
                      ? "text-brand-muted font-semibold"
                      : "text-white/55 hover:text-white hover:bg-white/10"
                  )}
                >
                  {sub.label}
                </Link>
              </li>
            ))}
          </ul>
        )}
      </li>
    );
  };

  const sidebarContent = (
    <div
      className={cn(
        "flex flex-col h-full bg-ink text-white border-r border-ink-700 transition-all duration-300",
        collapsed ? "w-16" : "w-64"
      )}
    >
      <div className="flex items-center justify-between px-4 h-16 border-b border-white/10">
        <Link
          to="/"
          onClick={onMobileClose}
          className="flex items-center gap-2.5 hover:opacity-90 transition-opacity"
          aria-label="Meridianos"
        >
          {/* Logo Meridianos (handoff Claude Design): el PNG es blanco/transparente
              y luce perfecto sobre el fondo `ink`. Si está colapsado mostramos
              solo el símbolo cuadrado del brand. */}
          {collapsed ? (
            <div className="w-7 h-7 rounded bg-brand flex-shrink-0" aria-hidden />
          ) : (
            <img
              src="/logo-meridianos.png"
              alt="Meridianos"
              className="h-7 w-auto object-contain"
            />
          )}
        </Link>
      </div>
      <nav className="flex-1 overflow-y-auto px-3 py-4">
        <ul className="space-y-0.5">{mainNavItems.map(renderNavItem)}</ul>
        <div className="my-4 border-t border-white/10" />
        {!collapsed && (
          <p className="px-3 mb-2 text-[10px] font-heading font-semibold uppercase tracking-[0.18em] text-white/40">
            Sistema
          </p>
        )}
        <ul className="space-y-0.5">{systemNavItems.map(renderNavItem)}</ul>
      </nav>
      <div className="border-t border-white/10">
        <div className="px-3 py-2 flex justify-end">
          <button
            onClick={onToggleCollapse}
            className="p-1.5 rounded-md text-white/45 hover:text-white hover:bg-white/10 transition-colors"
            aria-label={collapsed ? "Expandir sidebar" : "Colapsar sidebar"}
          >
            {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
          </button>
        </div>
        {!collapsed && (
          <div className="px-4 py-3 text-[11px] text-white/40 text-center font-heading tracking-wider uppercase">
            © 2026 Meridianos
          </div>
        )}
      </div>
    </div>
  );

  return (
    <>
      <aside className="hidden md:flex h-screen sticky top-0">{sidebarContent}</aside>
      {mobileOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={handleBackdropClick} aria-hidden="true" />
          <aside className="relative h-full w-64">{sidebarContent}</aside>
        </div>
      )}
    </>
  );
}
